/**
 * NotificationContext
 *
 * Provides real-time notification state via WebSocket + REST API.
 * Wraps useNotificationSocket and the /api/notifications endpoints.
 *
 * On `notification:new` WebSocket events, this context:
 *  - Prepends the notification to local state (instant UI update)
 *  - Increments unread count
 *  - Invalidates the React Query 'messagingUnreadCount' cache so the
 *    sidebar badge updates immediately instead of waiting for the next poll
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNotificationSocket, NotificationEvent } from '@/hooks/useNotificationSocket';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/services/api';

export interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  data: Record<string, unknown> | null;
  read: boolean;
  read_at: string | null;
  created_at: string;
}

interface NotificationContextValue {
  notifications: Notification[];
  unreadCount: number;
  isConnected: boolean;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  refresh: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return ctx;
}

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isAuthenticated = !!user;

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const { isConnected, onNotification } = useNotificationSocket({
    enabled: isAuthenticated,
  });

  // Fetch initial notifications and unread count
  const refresh = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const [notifRes, countRes] = await Promise.all([
        api.get('/notifications', { params: { limit: 20, includeRead: 'true' } }),
        api.get('/notifications/unread/count'),
      ]);

      if (notifRes.data?.success && notifRes.data?.data) {
        setNotifications(notifRes.data.data);
      }
      if (countRes.data?.success && countRes.data?.data) {
        setUnreadCount(countRes.data.data.count);
      }
    } catch {
      // Silent fail — polling will retry
    }
  }, [isAuthenticated]);

  // Initial load
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Subscribe to WebSocket events
  useEffect(() => {
    const unsubscribe = onNotification((event: NotificationEvent) => {
      // Add optimistic notification to local state
      const optimistic: Notification = {
        id: event.notificationId || `temp-${Date.now()}`,
        type: event.type,
        title: event.type === 'PATIENT_MESSAGE_RECEIVED' ? 'New patient message' : 'Notification',
        message: 'A patient has sent a new message. Open your inbox to view and respond.',
        data: {
          conversationId: event.conversationId || null,
          clinicId: event.clinicId || null,
        },
        read: false,
        read_at: null,
        created_at: event.timestamp || new Date().toISOString(),
      };

      setNotifications(prev => [optimistic, ...prev].slice(0, 50));
      setUnreadCount(prev => prev + 1);

      // Invalidate messaging unread count cache for instant sidebar badge update
      queryClient.invalidateQueries({ queryKey: ['messagingUnreadCount'] });
    });

    return unsubscribe;
  }, [onNotification, queryClient]);

  const markAsRead = useCallback(async (id: string) => {
    try {
      await api.put(`/notifications/${id}/read`);
      setNotifications(prev =>
        prev.map(n => (n.id === id ? { ...n, read: true, read_at: new Date().toISOString() } : n)),
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {
      // Silent fail
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      await api.put('/notifications/read-all');
      setNotifications(prev =>
        prev.map(n => ({ ...n, read: true, read_at: new Date().toISOString() })),
      );
      setUnreadCount(0);
    } catch {
      // Silent fail
    }
  }, []);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        isConnected,
        markAsRead,
        markAllAsRead,
        refresh,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}
